import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, FormHelperText, CircularProgress, useTheme, IconButton, Collapse } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AuthContext from "./auth/Context";

// Import thư viện Markdown
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// CSS tuỳ chỉnh cho thẻ sinh ra từ Markdown
const markdownStyles = {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: '0.75rem',
    color: 'text.secondary',
    '& p': { margin: '0 0 8px 0', '&:last-child': { margin: 0 } },
    '& code': { bgcolor: 'rgba(0,0,0,0.06)', p: '2px 4px', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.7rem' },
    '& pre': { bgcolor: 'rgba(0,0,0,0.06)', p: 1, borderRadius: 1, overflowX: 'auto', '& code': { bgcolor: 'transparent', p: 0 } },
    '& table': { borderCollapse: 'collapse', width: '100%', mb: 1 },
    '& th, & td': { border: '1px solid #ccc', p: 0.5 },
    '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
    '& ul, & ol': { margin: '0 0 8px 0', paddingLeft: '20px' }
};

const CodeEditor = ({
    onChange,
    onSave, 
    data: initialData = "",
    disabled = false,
    config,
    helperText,
    style,
    placeholder = "Nhập mã code vào đây...",
    debounceTime = 500
}) => {
    const auth = useContext(AuthContext);
    const theme = useTheme();

    const safeInitialData = typeof initialData === 'string' ? initialData : "";
    const [data, setData] = useState(safeInitialData);
    const [isFocused, setIsFocused] = useState(false);
    const [isHelperExpanded, setIsHelperExpanded] = useState(false);
    const [editorDeps, setEditorDeps] = useState({ Editor: null, highlight: null, languages: null });
    
    const timerRef = useRef(null);

    useEffect(() => { 
        setData(typeof initialData === 'string' ? initialData : ""); 
    }, [initialData]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadDependencies = async () => {
            try {
                const { default: Editor } = await import("react-simple-code-editor");
                const { highlight, languages } = await import('prismjs/components/prism-core');
                
                await import('prismjs/components/prism-clike');
                await import('prismjs/components/prism-javascript');
                await import('prismjs/themes/prism.css');

                if (isMounted) setEditorDeps({ Editor, highlight, languages });
            } catch (error) { console.error("Lỗi khi import Code Editor: ", error); }
        };
        loadDependencies();
        return () => { isMounted = false; };
    }, []);

    const handleValueChanged = useCallback((newValue) => {
        if (disabled) return;
        const safeValue = newValue || ""; 
        setData(safeValue);
        
        if (timerRef.current) clearTimeout(timerRef.current);
        
        timerRef.current = setTimeout(() => {
            if (onChange) {
                onChange({ target: { value: safeValue } }, safeValue);
            }
        }, debounceTime);

    }, [disabled, onChange, debounceTime]);

    const handleBlur = useCallback(() => {
        setIsFocused(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (onChange) {
            onChange({ target: { value: data } }, data); 
        }
    }, [onChange, data]);

    const handleFocus = useCallback(() => {
        if (!disabled) setIsFocused(true);
    }, [disabled]);

    const { Editor, highlight, languages } = editorDeps;

    if (!Editor) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, ...style }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    // Xử lý Helper Text (Markdown)
    const helperLines = helperText ? String(helperText).split('\n') : [];
    const isMultiLineHelper = helperLines.length > 1;
    const firstLine = helperLines.length > 0 ? helperLines[0] : '';

    return (
        <Box sx={{ width: '100%', ...style }}>
            <Box
                sx={{
                    position: 'relative',
                    border: '1px solid',
                    borderColor: isFocused ? theme.palette.primary.main : theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
                    borderRadius: 1, 
                    backgroundColor: disabled ? theme.palette.action.disabledBackground : 'transparent',
                    transition: theme.transitions.create(['border-color', 'box-shadow']),
                    '&:hover': { borderColor: !disabled && !isFocused ? theme.palette.text.primary : undefined },
                    ...(isFocused && { boxShadow: `${theme.palette.primary.main} 0 0 0 1px` }),
                    
                    '&::before': {
                       content: (!data && !isFocused && placeholder) ? `"${placeholder}"` : 'none',
                       position: 'absolute',
                       top: 12,
                       left: 12,
                       color: theme.palette.text.secondary,
                       opacity: 0.6,
                       pointerEvents: 'none', 
                       fontFamily: '"Fira code", "Fira Mono", monospace',
                       fontSize: 13,
                    }
                }}
            >
                <Editor
                    value={data || ""} 
                    onValueChange={handleValueChanged}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    disabled={disabled}
                    highlight={code => {
                        try { return highlight((code || "").toString(), languages.js); } 
                        catch (e) { return code || ""; }
                    }}
                    padding={12}
                    style={{
                        fontFamily: '"Fira code", "Fira Mono", monospace',
                        fontSize: 13,
                        minHeight: '120px', 
                        outline: 'none',    
                        opacity: disabled ? 0.6 : 1
                    }}
                />
            </Box>
            
            {/* Vùng hiển thị HelperText Markdown */}
            {helperText && (
                <Box sx={{ mt: 0.5, ml: 1.5, mr: 1.5, display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        
                        {!isMultiLineHelper ? (
                            <FormHelperText component="div" disabled={disabled} sx={{ mt: 0, mb: 0 }}>
                                <Box sx={markdownStyles}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{helperText}</ReactMarkdown>
                                </Box>
                            </FormHelperText>
                        ) : (
                            <>
                                {/* Chỉ hiển thị dòng đầu tiên nếu đang đóng */}
                                {!isHelperExpanded && (
                                    <FormHelperText component="div" disabled={disabled} sx={{ mt: 0, mb: 0, color: 'text.secondary', fontWeight: 500 }}>
                                        {firstLine} ...
                                    </FormHelperText>
                                )}
                                
                                {/* Hiển thị toàn bộ nội dung Markdown nếu mở */}
                                <Collapse in={isHelperExpanded} unmountOnExit>
                                    <FormHelperText 
                                        component="div"
                                        disabled={disabled} 
                                        sx={{ 
                                            mt: 0.5, 
                                            bgcolor: 'rgba(0,0,0,0.03)',
                                            p: 1,
                                            borderRadius: 1,
                                            border: '1px dashed #ccc'
                                        }}
                                    >
                                        <Box sx={markdownStyles}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{helperText}</ReactMarkdown>
                                        </Box>
                                    </FormHelperText>
                                </Collapse>
                            </>
                        )}
                    </Box>

                    {isMultiLineHelper && (
                        <IconButton 
                            size="small" 
                            onClick={() => setIsHelperExpanded(!isHelperExpanded)}
                            sx={{ mt: -0.5, ml: 1, color: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.08)' }}
                        >
                            {isHelperExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                    )}
                </Box>
            )}
        </Box>
    );
};

CodeEditor.propTypes = {
    onChange: PropTypes.func,
    onSave: PropTypes.func,
    data: PropTypes.string,
    disabled: PropTypes.bool,
    config: PropTypes.any,
    helperText: PropTypes.string,
    style: PropTypes.object,
    placeholder: PropTypes.string,
    debounceTime: PropTypes.number,
};

export default CodeEditor;